// Login redirect kit: the login URL with a return path, the open-redirect guard for that return path, the login page
// path, and the "this logout is on purpose" flag. Pure (relative import only), so it is tested and works on the server
// and in the browser. Moved from chawk-bazar / ciseco / jument (`helpers/accountAuth.js`, `loginUrl.js`,
// `safeRedirect.js`, `authRoute.js`) and jument `helpers/sellerRequest.js#safe_internal_path`.
import { is_locale_code, strip_locale_prefix } from "./locale-rules.js";

// The query key the login page reads. Do not change it: links already sent and open tabs carry it.
export const LOGIN_REDIRECT_PARAM = "redirect-to";

export const DEFAULT_AUTH_PATH = "/auth";

const strip_trailing_slashes = (value) => String(value ?? "").replace(/\/+$/, "");

// The leading language segment of a path: one of `locales` when given, otherwise any language code
// (`is_locale_code`). Used only to compare paths, never to build one.
const strip_any_locale = (pathname, locales) => {
	if (typeof pathname !== "string") return pathname;
	const segment = pathname.split("/")[1] || "";
	const is_locale = Array.isArray(locales) ? locales.includes(segment) : is_locale_code(segment);
	return is_locale ? strip_locale_prefix(pathname, segment) : pathname;
};

/**
 * The login page URL with `?redirect-to=<return_path>`. Slashes stay readable in the query; every other special
 * character (`?`, `&`, `#`) is encoded so it does not mix with the login page's own query.
 * (chawk-bazar / ciseco / jument, identical)
 */
export const build_login_url = (auth_path, return_path) => {
	const base = auth_path || `${DEFAULT_AUTH_PATH}/`;
	if (!return_path) return base;
	return `${base}?${LOGIN_REDIRECT_PARAM}=${encodeURIComponent(return_path).replace(/%2F/gi, "/")}`;
};

/**
 * The path to come back to after login: `pathname` with the page's query. `search_params` is a URLSearchParams or
 * Next's awaited `searchParams` object (the first value of an array is kept). (ciseco `loginUrl.js`, chawk-bazar
 * `accountPages.js`)
 */
export const build_return_path = (pathname, search_params) => {
	const query = new URLSearchParams();
	const entries =
		search_params && typeof search_params.entries === "function"
			? Array.from(search_params.entries())
			: Object.entries(search_params || {}).map(([key, value]) => [key, Array.isArray(value) ? value[0] : value]);
	for (const [key, value] of entries) if (typeof value === "string") query.append(key, value);
	const search = query.toString();
	return search ? `${pathname}?${search}` : pathname;
};

// Any origin works for parsing a path: only "did it stay on this origin" matters.
const PARSE_ORIGIN = "https://internal.invalid";

const safe_decode = (value) => {
	try {
		return decodeURIComponent(value);
	} catch {
		return null;
	}
};

/**
 * `value` when it is a path on this site, normalized (`/a/./b?x#y` → `/a/b?x#y`), otherwise null. The strictest of the
 * site copies (chawk-bazar / ciseco `safeRedirect.js`; jument's `safe_internal_path` only checked `//`):
 * - must start with `/`; parsed with the browser's URL parser, so `/\evil.com`, `/<tab>/evil.com`, `/.//evil.com`
 *   (all of which a browser reads as `//evil.com`) are rejected;
 * - control characters, backslashes and an encoded leading `//` (`/%2F/evil.com`, `/%5Cevil.com`) are rejected;
 * - the login page itself is rejected (a logged-in user would see the form again), unless `auth_path` is false.
 * @param options.origin the site origin; default `window.location.origin` in the browser (any origin works on the server)
 * @param options.auth_path the login page path (with or without a language prefix / trailing slash); false: no check
 * @param options.locales language codes for that comparison; default any language code
 */
export const get_safe_internal_path = (value, { origin, auth_path = DEFAULT_AUTH_PATH, locales } = {}) => {
	if (typeof value !== "string" || !value.startsWith("/")) return null;
	// eslint-disable-next-line no-control-regex
	if (/[\u0000-\u001F\u007F\\]/.test(value)) return null;

	const base = origin || (typeof window !== "undefined" && window.location?.origin) || PARSE_ORIGIN;
	let url;
	let site_origin;
	try {
		site_origin = new URL(base).origin;
		url = new URL(value, base);
	} catch {
		return null;
	}
	if (url.origin !== site_origin) return null;
	// `/.//evil.com` becomes `//evil.com` after dot segments are removed
	if (url.pathname.startsWith("//")) return null;

	const decoded = safe_decode(url.pathname);
	if (decoded === null || /^\/[/\\]/.test(decoded) || /[\u0000-\u001F\u007F]/.test(decoded)) return null;

	if (auth_path !== false && auth_path !== null) {
		const clean = (path) => strip_trailing_slashes(strip_any_locale(safe_decode(path) ?? path, locales));
		if (clean(url.pathname) === (clean(auth_path || DEFAULT_AUTH_PATH) || DEFAULT_AUTH_PATH)) return null;
	}

	return `${url.pathname}${url.search}${url.hash}`;
};

/**
 * The login page path from `web_route_names.auth` (default `/auth`), without the trailing slash. The language prefix
 * the core adds in a non-default language (`/en/auth/`) is kept, so the result can be used as a link (ciseco);
 * `strip_locale: true` removes it (chawk-bazar's copy did).
 */
export const get_auth_path = (route_names, { strip_locale = false, locales } = {}) => {
	const value = route_names?.["auth"];
	const path = typeof value === "string" && value.startsWith("/") ? value : DEFAULT_AUTH_PATH;
	return strip_trailing_slashes(strip_locale ? strip_any_locale(path, locales) : path) || DEFAULT_AUTH_PATH;
};

/**
 * Is `pathname` the login page? Both sides are compared without the language prefix and trailing slash, so it works
 * with a raw request path (`/en/auth/`) and with one already stripped (`init_page_server`'s `pathname`).
 * The login page does not depend on a CMS record, so it is recognised from the URL alone. (chawk-bazar / ciseco
 * `authRoute.js`; ciseco did not strip the prefix of the route name)
 * @param options.locales language codes to strip; default any language code
 */
export const is_auth_pathname = (pathname, route_names, { locales } = {}) => {
	if (typeof pathname !== "string") return false;
	const auth_path = get_auth_path(route_names, { strip_locale: true, locales });
	return strip_trailing_slashes(strip_any_locale(pathname, locales)) === auth_path;
};

/** Is `pathname` the login page or a page under it (`/auth/step/`)? Used to drop the return path on the login page. */
export const is_under_auth_path = (pathname, route_names, { locales } = {}) => {
	if (typeof pathname !== "string") return false;
	const auth_path = get_auth_path(route_names, { strip_locale: true, locales });
	const clean = strip_trailing_slashes(strip_any_locale(pathname, locales));
	return clean === auth_path || clean.startsWith(`${auth_path}/`);
};

// jument: the `seo.key`s of its account tabs (all served by the `page_name === "auth"` branch); the login page
// (`auth`) is not one of them.
export const ACCOUNT_PAGE_KEYS = Object.freeze([
	"account-page",
	"information-page",
	"favorite-page",
	"comments-page",
	"addresses-page",
	"transactions-page",
	"orders-page",
]);

// jument: is this `seo.key` an account tab? `keys` lets another site pass its own list.
export const is_account_page_key = (seo_key, keys = ACCOUNT_PAGE_KEYS) => Array.isArray(keys) && keys.includes(seo_key);

// The logout button sends the user home, while the account guard sends a guest to the login page and runs later, so it
// undid the logout navigation. The flag says "this logout is on purpose, its button picks the destination".
// Kept in this module (one copy per page); it lives only for one navigation. (chawk-bazar / ciseco / jument)
let logout_navigation_pending = false;
let logout_timer = null;

export const LOGOUT_NAVIGATION_MS = 3000;

export const mark_logout_navigation = (duration_ms = LOGOUT_NAVIGATION_MS) => {
	logout_navigation_pending = true;
	if (logout_timer) clearTimeout(logout_timer);
	logout_timer = setTimeout(() => {
		logout_navigation_pending = false;
		logout_timer = null;
	}, duration_ms);
};

export const is_logout_navigation_pending = () => logout_navigation_pending;
